import React, { useCallback, useState } from 'react';
import Button from '../../button';
import { ButtonSection, GroupBody, GroupModel } from './styles';
import { TaskGroupInterface } from '../../../interfaces/TaskInterface';
import { isEmpty } from '../../../helpers/common';
import { createNewGroup , updateGroupDetail } from '../../../services/taskServices';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import { PROJECT_ANALYTICS } from '../../../global/analyticsConstants';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { rootStore } from '../../../mobx/rootStore';
import { captureException } from '../../../services/logService';
import { nanoid } from 'nanoid';
import { BUTTON_TYPE } from '../../../global/constants';
import ModalHeader from '../modalHeader';
import Input from '../../input';

interface Props {
  groupData?: TaskGroupInterface | null;
  loadData?: () => void;
  onClose?: () => void;
  isTemplateProject?: boolean;
}

const CreateGroupModal: React.FC<Props> = (props) => {
  //Props declaration
  const { groupData, loadData, onClose, isTemplateProject } = props;

  //States
  const [groupName, setGroupName] = useState<string>(groupData?.name || '');
  const [loading, setLoading] = useState<boolean>(false);

  //Mobx store
  const { projectStore, projectTemplateStore } = rootStore || {};
  const { currentProject } = projectStore || {};
  const { currentProjectTemplate } = projectTemplateStore || {};

  //Handle group name change
  const onChangeInput = useCallback((value: string) => {
    setGroupName(value);
  }, []);

  //Handle create or edit group button
  const onCreateGroupButton = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      try {
        setLoading(true);
        const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
        let response;
        if (!isEmpty(groupData?.id)) {
          if (isTemplateProject) {
            const updatedGroups =
              currentProjectTemplate?.taskGroups?.map((item) => {
                if (item?.id === groupData?.id) {
                  return {
                    ...item,
                    name: groupName
                  };
                }
                return item;
              }) || [];
            currentProjectTemplate?.setTaskGroups(updatedGroups);
          } else {
            const payload = { userId: userDetails?.id, itemId: groupData?.id, name: groupName };
            response = await updateGroupDetail(payload);
          }
        } else {
          if (isTemplateProject) {
            const updatedGroups = [
              ...(currentProjectTemplate?.taskGroups || []),
              {
                id: String(nanoid()),
                name: groupName,
                projectId: currentProjectTemplate?.id || '',
                index: currentProjectTemplate?.taskGroups?.length || 0,
                isCollapse: false
              }
            ];
            currentProjectTemplate?.setTaskGroups(updatedGroups);
          } else {
            const payload = { userId: userDetails?.id, name: groupName, projectId: currentProject?.id };
            response = await createNewGroup(payload);
          }
        }
        if (response) {
          trackAnalyticActivity(PROJECT_ANALYTICS.GROUP_ADDED);
          loadData?.();
        }
      } catch (e) {
        captureException(e);
      } finally {
        onClose?.();
        setGroupName('');
        setLoading(false);
      }
    },
    [currentProject?.id, currentProjectTemplate, groupData?.id, groupName, isTemplateProject, loadData, onClose]
  );

  return (
    <GroupModel onSubmit={onCreateGroupButton}>
      <ModalHeader title={groupData?.id ? 'Update Group' : 'Create Group'} onClose={onClose} />
      <GroupBody>
        <Input
          label='Group name'
          autoFocus={true}
          placeHolder='Enter your group name'
          value={groupName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeInput(e.target.value)}
        />
        <ButtonSection>
          <Button
            variant={BUTTON_TYPE.PRIMARY}
            buttonType={'submit'}
            title={groupData?.id ? 'Update Group' : 'Create Group'}
            isLoading={loading}
          />
        </ButtonSection>
      </GroupBody>
    </GroupModel>
  );
};

export default CreateGroupModal;
